<template>
  <div
    class="mapping-select"
    v-on-clickaway="closeDropdown">
    <div
      class="mapping-select__trigger"
      :class="{ 'mapping-select__trigger--open': isOpen }"
      @click="toggleDropdown">
      <span
        class="mapping-select__value"
        :class="{ 'mapping-select__value--placeholder': !value }">
        {{ displayText }}
      </span>
      <Icon
        class="mapping-select__arrow"
        name="arrowDown"
        size="28" />
    </div>
    <div
      v-show="isOpen"
      class="mapping-select__dropdown">
      <input
        ref="searchInput"
        v-model="searchQuery"
        class="mapping-select__search"
        type="text"
        :placeholder="$t('Search...')"
        @keydown.esc="closeDropdown"
        @keydown.down.prevent="focusFirstOption" />
      <ul
        ref="optionsList"
        class="mapping-select__list">
        <li
          v-for="option in filteredOptions"
          :key="option.value"
          class="mapping-select__option"
          :class="{ 'mapping-select__option--active': option.value === value }"
          @click="selectOption(option)">
          {{ option.text }}
        </li>
        <li
          v-if="filteredOptions.length === 0"
          class="mapping-select__no-results">
          {{ $t('No results found') }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway';

export default {
  name: 'MappingSelect',
  directives: {
    onClickaway,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select field',
    },
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      randomId: `mapping-select-${Math.floor(100000 + Math.random() * 900000)}`,
    };
  },
  computed: {
    displayText() {
      if (!this.value) {
        return this.$t(this.placeholder);
      }
      const selected = this.options.find(opt => opt.value === this.value);
      return selected ? selected.text : this.value;
    },
    filteredOptions() {
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(option => {
        // Show option if: not selected OR is the current value
        const isAvailable = !option.selected || option.value === this.value;
        // Match search query
        const matchesSearch = !query || option.text.toLowerCase().includes(query);
        return isAvailable && matchesSearch;
      });
    },
  },
  methods: {
    toggleDropdown() {
      if (this.isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },
    openDropdown() {
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    closeDropdown() {
      this.isOpen = false;
      this.searchQuery = '';
    },
    selectOption(option) {
      this.$emit('input', option.value);
      this.$emit('change', option.value);
      this.closeDropdown();
    },
    focusFirstOption() {
      const firstOption = this.$refs.optionsList?.querySelector('.mapping-select__option');
      if (firstOption) {
        firstOption.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$border-color: #DBDFE3;
$text-color: #363D44;
$placeholder-color: #989FA6;
$focus-border: #FF9300;
$hover-bg: #F2F4F6;
$white: #fff;

.mapping-select {
  position: relative;
  width: 100%;

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 45px;
    padding: 0 45px 0 16px;
    background: $white;
    border: 1px solid $border-color;
    border-radius: 4px;
    cursor: pointer;
    position: relative;

    &:hover {
      border-color: darken($border-color, 10%);
    }

    &--open {
      border-color: $focus-border;
    }
  }

  &__value {
    font-size: 12px;
    color: $text-color;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;

    &--placeholder {
      color: $placeholder-color;
    }
  }

  &__arrow {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 11;
    margin-top: 4px;
    padding: 10px;
    background: $white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-width: 250px;
  }

  &__search {
    width: 100%;
    height: 36px;
    padding: 8px 12px;
    font-size: 12px;
    border: 1px solid $border-color;
    border-radius: 4px;
    outline: none;
    margin-bottom: 10px;

    &:focus {
      border-color: $focus-border;
    }

    &::placeholder {
      color: $placeholder-color;
    }
  }

  &__list {
    max-height: 300px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: $hover-bg;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: $border-color;
      border-radius: 3px;
    }
  }

  &__option {
    padding: 10px;
    font-size: 12px;
    color: $text-color;
    cursor: pointer;
    border-radius: 4px;

    &:hover,
    &--active {
      background-color: $hover-bg;
    }

    &:focus {
      outline: none;
      background-color: $hover-bg;
    }
  }

  &__no-results {
    padding: 10px;
    font-size: 12px;
    color: $placeholder-color;
    text-align: center;
  }
}
</style>
